import { TrackingKeys } from '../../util/tracking-keys';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    api,
    Constants,
    AppInsights,
    Tenant as TenantModel,
    TenantAddon,
    TermsAndCondition,
} from '@bluedolphin/util-module-mf';

const { AUTH_TENANT_KEY } = Constants;

type AuthB2CPolicy = {
    b2cPolicyName: string;
    b2cPolicy: string;
    image: string;
    isVendor: boolean;
    id: string;
    b2cClientId: string;
    b2cInstance: string;
    b2cTenant: string;
};

type TenantMeta = Pick<
    TenantModel,
    | 'b2Cpolicies'
    | 'is_expired'
    | 'name'
    | 'language'
    | 'friendly_name'
    | 'is_process_publication'
>;
export class Tenant {
    addons: TenantAddon[];
    apiVersion: number;
    b2CPolicies: AuthB2CPolicy[];
    b2CVendor: AuthB2CPolicy;
    friendlyName: string;
    isExpired: boolean;
    language: string;
    name: string;
    termsAndConditions: TermsAndCondition | null;
    version: number;

    constructor(data: TenantModel | TenantMeta) {
        const b2cPolicies: AuthB2CPolicy[] = [];
        data.b2Cpolicies.forEach((p) => {
            if (!data.is_process_publication) {
                const b2c_instance = p.b2c_instance.instance.split('/{0}')[0];
                b2cPolicies.push({
                    b2cPolicyName: p.b2c_policy_name,
                    b2cPolicy: `${b2c_instance}/${p.b2c_instance.tenant}/${p.b2c_policy_name}`,
                    image: p.image,
                    isVendor: p.is_vendor,
                    id: `${p.b2c_policy_name}_id`,
                    b2cClientId: p.b2c_instance.client_id,
                    b2cInstance: p.b2c_instance.instance,
                    b2cTenant: p.b2c_instance.tenant,
                });
            }
        });
        const nonVendors = b2cPolicies.filter((policy) => !policy.isVendor);
        const vendor = b2cPolicies.find((policy) => policy.isVendor);

        this.b2CPolicies = nonVendors;
        this.b2CVendor = vendor;
        this.name = data.name;
        this.addons = 'addons' in data ? data.addons : undefined;
        this.apiVersion = 'api_version' in data ? data.api_version : undefined;
        this.friendlyName = data.friendly_name;
        this.isExpired = data.is_expired;
        this.language = data.language;
        this.termsAndConditions =
            'terms_and_conditions' in data
                ? data.terms_and_conditions
                : undefined;
        this.version = 'version' in data ? data.version : undefined;
    }
}

type TenantContextValue = {
    tenant: InstanceType<typeof Tenant>;
    setTenantName(tenant: string): void;
    setIsOfficialProcess(value: boolean): void;
    updateTenant(): Promise<void>;
    tenantMeta: InstanceType<typeof Tenant>;
};

export const TenantContext = React.createContext<TenantContextValue>(null);

/**
 * Tenant Context Provider
 *
 * @returns {JSX.Element}
 */
export function TenantProvider({ children }): JSX.Element {
    const [isOfficialProcess, setIsOfficialProcess] = useState<boolean>(false);
    const [tenant, setTenant] = useState<InstanceType<typeof Tenant>>(null);
    const [tenantName, setTenantName] = useState<string>(null);
    const { i18n } = useTranslation();
    const [tenantMeta, setTenantMeta] = useState<Tenant>(null);
    useEffect(() => {
        if (tenantName && tenantName !== '404') {
            window.sessionStorage.setItem(AUTH_TENANT_KEY, tenantName);
            (async function fetchTenant() {
                try {
                    const { data } = await api.get<TenantMeta>({
                        url: '/tenant/meta',
                        headers: {
                            Tenant: tenantName,
                        },
                    });
                    api.setTenant(tenantName);
                    setTenantMeta(new Tenant(data));
                    i18n.changeLanguage(data.language.toLowerCase());
                    AppInsights.addTelemetryInitializer(tenantName);
                    AppInsights.trackEvent(TrackingKeys.AuthTenantXhrSuccess, {
                        tenantName,
                    });
                } catch (error) {
                    window.sessionStorage.removeItem(AUTH_TENANT_KEY);
                    AppInsights.trackEvent(TrackingKeys.AuthTenantXhrError, {
                        tenantName,
                        error,
                    });
                    window.history.pushState({}, '', '/404');
                }
            })();
        }
    }, [tenantName]);

    /**
     * Updates tenant policies when user is no authenticated and accesses to
     * process portal
     */
    useEffect(() => {
        if (isOfficialProcess && tenantMeta) {
            tenantMeta.b2CPolicies = tenantMeta.b2CPolicies.filter(isVendor);
            tenantMeta.b2CVendor = null;
            setTenant(tenantMeta);
        }
    }, [isOfficialProcess, tenantMeta]);

    const updateTenant = async () => {
        try {
            if (tenantName) {
                const { data } = await api.get<TenantModel>({
                    url: '/tenant',
                    headers: {
                        Tenant: tenantName,
                    },
                });
                setTenant(new Tenant(data));
            }
        } catch (error) {
            AppInsights.trackEvent(TrackingKeys.AuthTenantXhrError, {
                tenantName,
                error,
            });
        }
    };
    /**
     * Filters valueblue and bluedolphin policies
     *
     * @param {Record<string, any>} policy
     */
    function isVendor(policy: { b2cPolicyName: string }) {
        return (
            !policy.b2cPolicyName.includes('valueblue') &&
            !policy.b2cPolicyName.includes('bluedolphin')
        );
    }

    const contextValue = useMemo(
        () => ({
            setIsOfficialProcess,
            setTenantName,
            tenant,
            tenantMeta,
            updateTenant,
        }),
        [setIsOfficialProcess, setTenantName, tenant, tenantMeta, updateTenant]
    );
    return (
        <TenantContext.Provider value={contextValue}>
            {children}
        </TenantContext.Provider>
    );
}

/**
 * A wrapper around the useContext Hook
 * Returns the tenant context data
 *
 * @returns {TenantContextValue}
 */
export function useTenant(): TenantContextValue {
    return useContext(TenantContext);
}
